import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { useToast } from '../hooks/use-toast';
import { 
  Bell, Mail, AlertTriangle, Clock, TrendingDown, Send, RefreshCw,
  Building, Users, DollarSign, Calendar, CheckCircle, XCircle,
  BarChart3, PieChart, Activity, FileText, Settings, History,
  Heart, Zap, AlertCircle, ChevronRight
} from 'lucide-react';
import PageHeader from './ui/PageHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AlertsReportsPage = () => {
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [trialExpiring, setTrialExpiring] = useState([]);
  const [paymentFailures, setPaymentFailures] = useState([]);
  const [healthSummary, setHealthSummary] = useState(null);
  const [atRiskCompanies, setAtRiskCompanies] = useState([]);
  const [alertHistory, setAlertHistory] = useState([]);
  const [alertStats, setAlertStats] = useState(null);
  
  // Settings
  const [trialDays, setTrialDays] = useState(7);
  const [healthThreshold, setHealthThreshold] = useState(40);

  const getToken = () => localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${getToken()}` };
      
      const [trialRes, paymentRes, healthRes, atRiskRes, historyRes, statsRes] = await Promise.all([
        axios.get(`${API}/alerts-reports/billing/trial-expiring?days=${trialDays}`, { headers }),
        axios.get(`${API}/alerts-reports/billing/payment-failures`, { headers }),
        axios.get(`${API}/alerts-reports/health-scores/summary`, { headers }),
        axios.get(`${API}/alerts-reports/health-scores/at-risk?threshold=${healthThreshold}`, { headers }),
        axios.get(`${API}/alerts-reports/history?limit=50`, { headers }),
        axios.get(`${API}/alerts-reports/stats`, { headers })
      ]);
      
      setTrialExpiring(trialRes.data.companies || []);
      setPaymentFailures(paymentRes.data.companies || []);
      setHealthSummary(healthRes.data);
      setAtRiskCompanies(atRiskRes.data.companies || []);
      setAlertHistory(historyRes.data.alerts || []);
      setAlertStats(statsRes.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load alerts data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [trialDays, healthThreshold, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sendTrialReminders = async () => {
    try {
      setSending(true);
      await axios.post(`${API}/alerts-reports/billing/send-trial-reminders`, null, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast({ title: "Success", description: "Trial reminder emails queued for sending" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to send reminders", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const sendPaymentAlerts = async () => {
    try {
      setSending(true);
      await axios.post(`${API}/alerts-reports/billing/send-payment-failure-alerts`, null, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast({ title: "Success", description: "Payment failure alerts queued for sending" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to send alerts", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const sendHealthReport = async () => {
    try {
      setSending(true);
      await axios.post(`${API}/alerts-reports/health-scores/send-report?threshold=${healthThreshold}`, null, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast({ title: "Success", description: "Health score report sent to admins" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to send report", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const sendLowScoreAlerts = async () => {
    try {
      setSending(true);
      await axios.post(`${API}/alerts-reports/health-scores/send-low-score-alerts?threshold=30`, null, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast({ title: "Success", description: "Low health score alerts sent" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to send alerts", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const getScoreColor = (score) => {
    if (score > 75) return 'bg-green-500';
    if (score > 50) return 'bg-yellow-500';
    if (score > 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getAlertTypeIcon = (type) => {
    if (type?.includes('trial')) return <Clock className="w-4 h-4 text-blue-500" />;
    if (type?.includes('payment')) return <DollarSign className="w-4 h-4 text-red-500" />;
    if (type?.includes('health')) return <Heart className="w-4 h-4 text-purple-500" />;
    return <Bell className="w-4 h-4 text-gray-500" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Alerts & Reports"
        subtitle="Automated billing alerts and company health score reports"
        icon={Bell}
        variant="purple"
        onRefresh={fetchData}
        loading={loading}
        stats={alertStats ? [
          { icon: Bell, label: 'Total Alerts', value: alertStats.total_alerts, color: 'blue' },
          { icon: Mail, label: 'Emails Sent', value: alertStats.emails_sent, color: 'green' },
          { icon: AlertTriangle, label: 'Trial Expiring', value: trialExpiring.length, color: 'yellow' },
          { icon: Heart, label: 'At Risk', value: atRiskCompanies.length, color: 'red' }
        ] : []}
      />

      <Tabs defaultValue="billing" className="space-y-6">
        <TabsList className="bg-white border rounded-xl p-1">
          <TabsTrigger value="billing" className="rounded-lg">
            <DollarSign className="w-4 h-4 mr-2" /> Billing Alerts
          </TabsTrigger>
          <TabsTrigger value="health" className="rounded-lg">
            <Heart className="w-4 h-4 mr-2" /> Health Scores
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-lg">
            <History className="w-4 h-4 mr-2" /> Alert History
          </TabsTrigger>
        </TabsList>

        {/* Billing Alerts Tab */}
        <TabsContent value="billing" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Trial Expiring */}
            <Card className="rounded-2xl">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    <Clock className="w-5 h-5 mr-2 text-blue-600" />
                    Trial Expiring Soon
                  </CardTitle>
                  <CardDescription>Companies with trials ending in {trialDays} days</CardDescription>
                </div>
                <Button 
                  onClick={sendTrialReminders} 
                  disabled={sending || trialExpiring.length === 0}
                  size="sm"
                >
                  {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Reminders
                </Button>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Label>Days Threshold</Label>
                  <Select value={trialDays.toString()} onValueChange={(v) => setTrialDays(parseInt(v))}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 days</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {trialExpiring.length > 0 ? (
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {trialExpiring.map((company, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div>
                          <p className="font-medium">{company.name}</p>
                          <p className="text-sm text-gray-500">{company.email}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={company.days_remaining <= 1 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}>
                            {company.days_remaining} day{company.days_remaining !== 1 ? 's' : ''} left
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-300" />
                    <p>No trials expiring in the next {trialDays} days</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Failures */}
            <Card className="rounded-2xl">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
                    Payment Failures
                  </CardTitle>
                  <CardDescription>Companies with failed or past due payments</CardDescription>
                </div>
                <Button 
                  onClick={sendPaymentAlerts} 
                  disabled={sending || paymentFailures.length === 0}
                  size="sm"
                  variant="destructive"
                >
                  {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Alerts
                </Button>
              </CardHeader>
              <CardContent>
                {paymentFailures.length > 0 ? (
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {paymentFailures.map((company, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium">{company.name}</p>
                          <p className="text-sm text-gray-500">{company.email || 'No email'}</p>
                        </div>
                        <div className="text-right">
                          <Badge variant="destructive">
                            {company.subscription_status}
                          </Badge>
                          <p className="text-xs text-gray-500 mt-1">{company.plan_name || 'Unknown plan'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-300" />
                    <p>No payment failures detected</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Health Scores Tab */}
        <TabsContent value="health" className="space-y-6">
          {/* Health Summary Cards */}
          {healthSummary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="rounded-xl border-t-4 border-t-green-500">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Healthy (76-100)</p>
                      <p className="text-2xl font-bold text-green-600">{healthSummary.distribution?.healthy?.count || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-xl border-t-4 border-t-yellow-500">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Moderate (51-75)</p>
                      <p className="text-2xl font-bold text-yellow-600">{healthSummary.distribution?.moderate?.count || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                      <Activity className="w-6 h-6 text-yellow-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-xl border-t-4 border-t-orange-500">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Warning (26-50)</p>
                      <p className="text-2xl font-bold text-orange-600">{healthSummary.distribution?.warning?.count || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-xl border-t-4 border-t-red-500">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Critical (0-25)</p>
                      <p className="text-2xl font-bold text-red-600">{healthSummary.distribution?.critical?.count || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                      <XCircle className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* At Risk Companies & Report Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* At Risk List */}
            <Card className="rounded-2xl lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    <TrendingDown className="w-5 h-5 mr-2 text-red-600" />
                    At-Risk Companies
                  </CardTitle>
                  <CardDescription>Companies with health score ≤ {healthThreshold}</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={healthThreshold.toString()} onValueChange={(v) => setHealthThreshold(parseInt(v))}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">≤ 25</SelectItem>
                      <SelectItem value="40">≤ 40</SelectItem>
                      <SelectItem value="50">≤ 50</SelectItem>
                      <SelectItem value="60">≤ 60</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={sendLowScoreAlerts} 
                    disabled={sending || atRiskCompanies.length === 0}
                    size="sm"
                    variant="outline"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Alert Companies
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {atRiskCompanies.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {atRiskCompanies.map((company, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getScoreColor(company.health_score)} text-white font-bold`}>
                              {company.health_score}
                            </div>
                          </div>
                          <div>
                            <p className="font-medium">{company.name}</p>
                            <p className="text-sm text-gray-500">{company.industry} • {company.employee_count || 0} employees</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline">{company.subscription_status}</Badge>
                          <p className="text-xs text-gray-500 mt-1">{company.total_logins || 0} logins</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-300" />
                    <p>No at-risk companies found</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Report Actions */}
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-purple-600" />
                  Report Actions
                </CardTitle>
                <CardDescription>Send reports to administrators</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-purple-50 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium">Health Score Report</p>
                      <p className="text-sm text-gray-500">Summary of all company health scores</p>
                    </div>
                  </div>
                  <Button 
                    onClick={sendHealthReport} 
                    disabled={sending}
                    className="w-full"
                  >
                    {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Report to Admins
                  </Button>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl">
                  <h4 className="font-medium mb-2">Average Score</h4>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <Progress value={healthSummary?.average_health_score || 0} className="h-3" />
                    </div>
                    <span className="font-bold text-lg">{(healthSummary?.average_health_score || 0).toFixed(1)}</span>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl">
                  <h4 className="font-medium mb-2">Total Companies</h4>
                  <p className="text-3xl font-bold text-purple-600">{healthSummary?.total_companies || 0}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Alert History Tab */}
        <TabsContent value="history">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center">
                <History className="w-5 h-5 mr-2 text-gray-600" />
                Alert History
              </CardTitle>
              <CardDescription>Recent alerts and notifications sent</CardDescription>
            </CardHeader>
            <CardContent>
              {alertHistory.length > 0 ? (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {alertHistory.map((alert, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        {getAlertTypeIcon(alert.alert_type)}
                        <div>
                          <p className="font-medium">{alert.message}</p>
                          <p className="text-sm text-gray-500">
                            {alert.company_name && `${alert.company_name} • `}
                            {new Date(alert.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {alert.email_sent && (
                          <Badge className="bg-green-100 text-green-700">
                            <Mail className="w-3 h-3 mr-1" />
                            Sent
                          </Badge>
                        )}
                        <Badge variant="outline">{alert.alert_type}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <History className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No alert history found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AlertsReportsPage;
